import { Link } from "react-router-dom";
import { FileText, ShieldCheck, RefreshCw as RefreshIcon } from "lucide-react";

const CONTACT_EMAIL = "TeacherUtilityToolKit@gmail.com";
const OWNER_NAME = "PM Poshan Calculator";
const DOMAIN = "pmposhancalculator.com";

export function Terms() {
  return (
    <Page title="Terms & Conditions" icon={<FileText size={16}/>} chip="Legal · Terms" data-testid="terms-page">
      <p className="text-slate-500 text-sm">Last updated: 22 September 2026</p>
      <Section title="1. Service">
        <p>{OWNER_NAME} (this website, {DOMAIN}) is an independent digital utility that helps school HMs and teachers run PM POSHAN Mid-Day Meal calculations, generate PDF reports, maintain a Poshan Panji register, and compute Nutrition Ladoo statements. We are <b>not affiliated with, endorsed by, or authorised by</b> the Government of India, the Government of Odisha, or the PM POSHAN scheme administration. Rates and menus are based on publicly available norms — teachers must verify against official circulars.</p>
      </Section>
      <Section title="2. Accounts">
        <p>You may use the anonymous calculator, Ladoo calculator and Poshan Panji-by-UDISE tools without registering. To save Daily Entries, view history and run reports you need to create a free account with a valid Indian mobile number and password. You are responsible for keeping your credentials and recovery details safe. Do not share your account.</p>
      </Section>
      <Section title="3. Premium subscription (₹299 / year · Launch Offer)">
        <p>Premium unlocks Daily Entry saving, Daily History, Weekly/Monthly/Annual reports, Bulk PDF, School profile and personal register — everything for one year from the payment date. There is no auto-renewal. Payment is processed by Razorpay; we never see your card / UPI credentials.</p>
      </Section>
      <Section title="4. Free-report limit">
        <p>Free users can generate up to <b>7 report generations</b> per usage identity (a combination of IP and, if signed in, mobile number). After that a Premium activation screen appears. This limit exists to keep the free tier available and prevent misuse.</p>
      </Section>
      <Section title="5. Referral program">
        <p>Registered users receive a unique referral code (format <code>PM-XXXXXX</code>). A referral counts as <b>successful</b> only when a new genuine user registers through the referral link/code and completes a server-verified Premium purchase that is not refunded, reversed or cancelled. Registration alone, failed payments and refunded payments do not qualify. Self-referrals (same account or same mobile) and clearly duplicate/abusive accounts are rejected. Each referred user contributes at most one successful referral credit. Reaching <b>50 successful referrals</b> unlocks <b>365 days of Premium free</b> — added to any existing Premium expiry, never replacing it. Suspicious referrals may be placed <i>Under review</i> and adjudicated by the admin. Refunds after a milestone has been granted are flagged for admin review rather than silently stripped. The rules may change for future referrals; already-verified rewards follow the rules in force at the time they were earned.</p>
      </Section>
      <Section title="5. Data accuracy">
        <p>Calculations reflect published Odisha norms as of the day the rate table was last updated by the admin. You are responsible for verifying quantities and cost against official circulars before submission. Under no circumstances shall the site owner be liable for any consequence arising from reliance on the outputs.</p>
      </Section>
      <Section title="6. Acceptable use">
        <p>You agree not to reverse engineer, scrape at scale, redistribute the paid features, attempt to bypass the free-report limit or interfere with other users. Abuse may result in account suspension without refund.</p>
      </Section>
      <Section title="7. Suspension and termination">
        <p>We may deactivate accounts that violate these terms, are used fraudulently, or upon a valid legal request. You may delete your account at any time by writing to us.</p>
      </Section>
      <Section title="8. Changes">
        <p>We may update these Terms from time to time. Continued use after an update constitutes acceptance. Material changes will be flagged on the home page.</p>
      </Section>
      <Section title="9. Contact">
        <p>Support: <a className="text-emerald-700 font-semibold" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
      </Section>
    </Page>
  );
}

export function Privacy() {
  return (
    <Page title="Privacy Policy" icon={<ShieldCheck size={16}/>} chip="Legal · Privacy" data-testid="privacy-page">
      <p className="text-slate-500 text-sm">Last updated: 22 September 2026</p>
      <Section title="1. Data we collect">
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Account:</b> full name, mobile number, optional email, school name, UDISE code.</li>
          <li><b>Recovery:</b> your 3 chosen recovery numbers and 3 image ids — stored as a bcrypt hash. We cannot read them back.</li>
          <li><b>Payments:</b> Razorpay order id, payment id, amount, currency, status, timestamps. We do NOT store card / UPI credentials.</li>
          <li><b>Daily entries:</b> the attendance & calculation results you choose to save under your account.</li>
          <li><b>Usage limit:</b> a SHA-256 hash of your IP and (if signed in) your mobile number, plus a counter — used to enforce the 7-free-report rule.</li>
          <li><b>Technical logs:</b> timestamps and error metadata (no third-party analytics is currently active).</li>
        </ul>
      </Section>
      <Section title="2. How we use it">
        <p>To provide the service, deliver PDF reports, activate your Premium subscription, prevent abuse, send you receipts (if email provided), and provide support. We do not sell or share your data for advertising.</p>
      </Section>
      <Section title="3. Third parties">
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Razorpay</b> processes payments — subject to their <a className="text-emerald-700 font-semibold" href="https://razorpay.com/privacy/" target="_blank" rel="noreferrer">privacy policy</a>.</li>
          <li><b>MongoDB</b> hosts our database on secure managed infrastructure.</li>
        </ul>
      </Section>
      <Section title="4. Security">
        <p>Passwords and recovery answers are hashed with bcrypt. Payment signatures are HMAC-SHA256 verified server-side. Data is transmitted over HTTPS.</p>
      </Section>
      <Section title="5. Retention">
        <p>Account data is retained while the account is active. On request we will delete your account, daily entries and any linked recovery data within 30 days (payment history is retained for statutory accounting periods).</p>
      </Section>
      <Section title="6. Your rights">
        <p>You may view, edit or delete your profile, update your recovery details, change your password, and request account deletion by writing to <a className="text-emerald-700 font-semibold" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
      </Section>
      <Section title="7. Contact">
        <p>Privacy queries: <a className="text-emerald-700 font-semibold" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
      </Section>
    </Page>
  );
}

export function Refund() {
  return (
    <Page title="Refund Policy" icon={<RefreshIcon size={16}/>} chip="Legal · Refund" data-testid="refund-page">
      <p className="text-slate-500 text-sm">Last updated: 22 September 2026</p>
      <Section title="1. Subscription">
        <p>Premium costs ₹299 for one full year (Launch Offer; regular price ₹1,999) and unlocks Save-Daily-Entry, Daily History, Weekly/Monthly/Annual reports and Bulk PDF. Please try the free calculator and ladoo tools thoroughly before purchasing.</p>
      </Section>
      <Section title="2. Refund eligibility">
        <p>We offer refunds in the following cases only:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Duplicate payment</b> caused by a technical issue where the user was charged twice for the same account.</li>
          <li><b>Failed activation</b> — payment was captured by Razorpay but Premium was not activated on your account within 24 hours.</li>
          <li><b>Fraudulent charge</b> — reported within 7 days of the transaction.</li>
        </ul>
      </Section>
      <Section title="3. Non-refundable cases">
        <p>Change-of-mind after Premium is activated, dissatisfaction with features that are clearly listed on the Pricing page, and lapses caused by user error (wrong mobile, forgotten credentials) do not qualify for a refund.</p>
      </Section>
      <Section title="4. How to request a refund">
        <ol className="list-decimal pl-5 space-y-1">
          <li>Email <a className="text-emerald-700 font-semibold" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> from the account&rsquo;s registered mobile number.</li>
          <li>Include the Razorpay Order ID / Payment ID.</li>
          <li>Describe the issue.</li>
        </ol>
      </Section>
      <Section title="5. Processing time">
        <p>Approved refunds are processed via Razorpay within 5–7 business days. The refund appears on the original payment method.</p>
      </Section>
      <Section title="6. Contact">
        <p>Refund support: <a className="text-emerald-700 font-semibold" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
      </Section>
    </Page>
  );
}

function Page({ title, icon, chip, children, ...props }) {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" {...props}>
      <div className="chip chip-green inline-flex">{icon} {chip}</div>
      <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">{title}</h1>
      <div className="mt-6 space-y-5 text-slate-700 leading-relaxed">{children}</div>
      <div className="mt-10 border-t border-slate-200 pt-6 text-xs text-slate-500 space-x-3">
        <Link to="/terms" className="hover:text-slate-700">Terms</Link>
        <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
        <Link to="/refund" className="hover:text-slate-700">Refund</Link>
        <Link to="/about" className="hover:text-slate-700">About</Link>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display font-bold text-lg text-slate-900">{title}</h2>
      <div className="mt-1.5 text-sm">{children}</div>
    </section>
  );
}
